import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Info } from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/Card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { Skeleton } from "@/components/ui/Skeleton";
import { FatalError } from "@/components/FatalError";
import { useMyProfile } from "@/hooks/useMyProfile";
import { useUpdateContactInfo } from "@/hooks/useUpdateContactInfo";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { AsYouType } from "libphonenumber-js";
import {
  validatePhoneNumberOnKeystroke,
  validateCompletePhoneNumber,
} from "@/util/phoneUtils";

interface ContactInfoFormData {
  email: string;
  phoneNumber: string;
}

export const EditContactInfo = () => {
  const { t } = useTranslation();
  const {
    profile,
    loading: profileLoading,
    error: profileError,
  } = useMyProfile();

  const [showSsoAlert, setShowSsoAlert] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] =
    useState<ContactInfoFormData | null>(null);

  const [updateContactInfo, { loading: updateLoading }] = useUpdateContactInfo({
    onError: (error) => {
      let errorMessage = t("global.unexpectedError");

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      }

      toast.error(t("manageProfile.contactInfo.updateError"), {
        description: errorMessage,
      });
    },
  });

  const {
    handleSubmit,
    register,
    reset,
    watch,
    setValue,
    formState: { isDirty, errors },
    setError,
    clearErrors,
  } = useForm<ContactInfoFormData>({
    defaultValues: {
      email: "",
      phoneNumber: "",
    },
    values: profile
      ? {
          email: profile.email || "",
          phoneNumber: profile.phoneNumber || "",
        }
      : undefined,
  });

  const phoneNumber = watch("phoneNumber");

  const [hasFormattedPhone, setHasFormattedPhone] = useState(false);

  useEffect(() => {
    if (profile?.phoneNumber && !hasFormattedPhone) {
      const formatter = new AsYouType();
      const formatted = formatter.input(profile.phoneNumber);
      setValue("phoneNumber", formatted);
      setHasFormattedPhone(true);
    }
  }, [profile?.phoneNumber, hasFormattedPhone, setValue]);

  useEffect(() => {
    const validation = validatePhoneNumberOnKeystroke(phoneNumber);

    if (!validation.isValid) {
      const errorKey =
        validation.error === "MUST_START_WITH_PLUS"
          ? "phoneMustStartWithPlus"
          : "phoneInvalid";

      setError("phoneNumber", {
        type: "manual",
        message: t(`manageProfile.contactInfo.errors.${errorKey}`),
      });
    } else {
      clearErrors("phoneNumber");
    }
  }, [phoneNumber, setError, clearErrors, t]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (!value || value === "+") {
      setValue("phoneNumber", value, { shouldDirty: true });
      return;
    }

    if (value.startsWith("+")) {
      const validation = validatePhoneNumberOnKeystroke(value);
      if (validation.isValid) {
        const formatter = new AsYouType();
        const formatted = formatter.input(value);
        setValue("phoneNumber", formatted, { shouldDirty: true });
      } else {
        setValue("phoneNumber", value, { shouldDirty: true });
      }
    } else {
      setValue("phoneNumber", value, { shouldDirty: true });
    }
  };

  const performSubmit = async (data: ContactInfoFormData) => {
    if (!profile?.id) return;

    const normalizedPhone = data.phoneNumber.startsWith("+")
      ? "+" + data.phoneNumber.slice(1).replace(/\D/g, "")
      : data.phoneNumber.replace(/\D/g, "");

    const result = await updateContactInfo({
      userId: profile.id,
      email: data.email.trim(),
      phoneNumber: normalizedPhone,
    });

    if (result?.errors?.length) {
      toast.error(t("manageProfile.contactInfo.updateError"), {
        description: result.errors[0].message,
      });
    } else {
      toast.success(t("manageProfile.contactInfo.updateSuccess"));
      reset({
        email: data.email.trim(),
        phoneNumber: data.phoneNumber,
      });
    }
  };

  const onSubmit = async (data: ContactInfoFormData) => {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!data.email) {
      setError("email", {
        type: "manual",
        message: t("manageProfile.contactInfo.errors.emailRequired"),
      });
      return;
    }
    if (!emailRegex.test(data.email)) {
      setError("email", {
        type: "manual",
        message: t("manageProfile.contactInfo.errors.emailInvalid"),
      });
      return;
    }

    if (!data.phoneNumber) {
      setError("phoneNumber", {
        type: "manual",
        message: t("manageProfile.contactInfo.errors.phoneRequired"),
      });
      return;
    }

    if (!validateCompletePhoneNumber(data.phoneNumber)) {
      setError("phoneNumber", {
        type: "manual",
        message: t("manageProfile.contactInfo.errors.phoneInvalid"),
      });
      return;
    }

    const emailChanged = data.email.trim() !== (profile?.email || "");
    if (emailChanged && profile?.ssoEnabled) {
      setPendingSubmitData(data);
      setShowSsoAlert(true);
      return;
    }

    await performSubmit(data);
  };

  const handleSsoAlertSave = async () => {
    setShowSsoAlert(false);
    if (pendingSubmitData) {
      await performSubmit(pendingSubmitData);
      setPendingSubmitData(null);
    }
  };

  const handleSsoAlertCancel = () => {
    setShowSsoAlert(false);
    setPendingSubmitData(null);
  };

  const handleCancel = () => {
    const rawPhone = profile?.phoneNumber || "";

    let formattedPhone = rawPhone;
    if (rawPhone) {
      const formatter = new AsYouType();
      formattedPhone = formatter.input(rawPhone);
    }

    reset({
      email: profile?.email || "",
      phoneNumber: formattedPhone,
    });
    clearErrors();
  };

  if (profileError) {
    return <FatalError />;
  }

  if (profileLoading) {
    return (
      <Card className="w-[760px]">
        <CardHeader>
          <CardTitle className="text-xl">
            {t("manageProfile.contactInfo.title")}
          </CardTitle>
          <CardDescription>
            {t("manageProfile.contactInfo.subTitle")}
          </CardDescription>
        </CardHeader>

        <CardContent className="flex flex-col gap-6 p-10 px-8">
          {/* Privacy Notice Skeleton */}
          <Skeleton className="h-16 w-full rounded-lg" />

          {/* Email Skeleton */}
          <div className="flex flex-col gap-2">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-10 w-full" />
          </div>

          {/* Phone Number Skeleton */}
          <div className="flex flex-col gap-2">
            <Skeleton className="h-5 w-24" />
            <Skeleton className="h-10 w-full" />
          </div>
        </CardContent>

        <CardFooter className="justify-end gap-3 border-t px-6 py-4">
          <Skeleton className="h-10 w-16" />
        </CardFooter>
      </Card>
    );
  }

  return (
    <>
      <Card className="w-[760px]">
        <CardHeader>
          <CardTitle className="text-xl">
            {t("manageProfile.contactInfo.title")}
          </CardTitle>
          <CardDescription>
            {t("manageProfile.contactInfo.subTitle")}
          </CardDescription>
        </CardHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <CardContent className="flex flex-col gap-6 p-10 px-8">
            {/* Privacy Notice */}
            <div className="flex items-start gap-3 rounded-lg bg-background p-4 outline outline-1 outline-offset-[-1px] outline-border">
              <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
              <p className="text-sm text-foreground">
                {t("manageProfile.contactInfo.privacyNotice")}
              </p>
            </div>

            {/* Email */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-foreground">
                {t("manageProfile.contactInfo.email")}
              </label>
              <input
                type="email"
                placeholder={t("manageProfile.contactInfo.emailHint")}
                {...register("email")}
                className={`h-10 rounded-md bg-background px-3 py-2 text-sm text-foreground outline outline-1 outline-offset-[-1px] ${
                  errors.email ? "outline-destructive" : "outline-input"
                }`}
              />
              {errors.email && (
                <p className="text-xs text-destructive">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Phone Number */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-foreground">
                {t("manageProfile.contactInfo.phoneNumber")}
              </label>
              <input
                type="tel"
                placeholder={t("manageProfile.contactInfo.phoneNumberHint")}
                value={phoneNumber}
                onChange={handlePhoneChange}
                className={`h-10 rounded-md bg-background px-3 py-2 text-sm text-foreground outline outline-1 outline-offset-[-1px] ${
                  errors.phoneNumber ? "outline-destructive" : "outline-input"
                }`}
              />
              {errors.phoneNumber && (
                <p className="text-xs text-destructive">
                  {errors.phoneNumber.message}
                </p>
              )}
            </div>
          </CardContent>

          <CardFooter className="justify-between border-t px-6 py-4">
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/10"
            >
              {t("global.cancel")}
            </button>
            <button
              type="submit"
              disabled={
                !isDirty ||
                updateLoading ||
                !!errors.email ||
                !!errors.phoneNumber
              }
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t("global.save")}
            </button>
          </CardFooter>
        </form>
      </Card>

      {/* SSO Alert Dialog */}
      <AlertDialog open={showSsoAlert} onOpenChange={setShowSsoAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("manageProfile.contactInfo.ssoAlert.title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("manageProfile.contactInfo.ssoAlert.message")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleSsoAlertCancel}>
              {t("global.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleSsoAlertSave}>
              {t("global.save")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
